import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Users, Award, ThumbsUp, Shield } from "lucide-react";

function AnimatedCounter({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {count.toLocaleString("tr-TR")}
      {suffix}
    </span>
  );
}

const stats = [
  { icon: Users, value: 10000, suffix: "+", label: "Mutlu Danışan" },
  { icon: Award, value: 5, suffix: "+", label: "Yıllık Deneyim" },
  { icon: ThumbsUp, value: 100, suffix: "%", label: "Memnuniyet" },
];

const features = [
  { icon: Users, title: "Uzman Ekip", desc: "Alanında deneyimli profesyonel kadro" },
  { icon: Shield, title: "Hijyenik Ortam", desc: "En yüksek hijyen standartlarında hizmet" },
  { icon: Award, title: "Premium Cihazlar", desc: "Son teknoloji cihazlar ile bakım" },
  { icon: ThumbsUp, title: "%100 Memnuniyet", desc: "Müşteri memnuniyeti önceliğimiz" },
];

export default function WhyChooseUs() {
  return (
    <section
      data-testid="why-choose-section"
      className="py-24 md:py-32 bg-[#FAFAFA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-20"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] gold-text mb-4">
            Neden Biz?
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-black">
            Güvenilir Bakımın Adresi
          </h2>
        </motion.div>

        {/* Animated Counters */}
        <div className="grid grid-cols-3 gap-4 md:gap-6 mb-16 md:mb-20">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              data-testid={`stat-${i}`}
              className="text-center"
            >
              <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tighter gold-text mb-1 md:mb-2">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              </div>
              <p className="text-xs sm:text-sm font-medium text-black/60 tracking-wide">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`feature-${i}`}
              className="p-8 bg-white border border-[#EBEBEB] hover:shadow-[0_20px_40px_rgba(0,0,0,0.05)] transition-all duration-500"
            >
              <div className="w-12 h-12 flex items-center justify-center gold-bg mb-5">
                <feature.icon size={20} className="text-white" />
              </div>
              <h3 className="text-base font-bold tracking-tight text-black mb-2">
                {feature.title}
              </h3>
              <p className="text-sm leading-relaxed text-black/70">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
